import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Button,
  Center,
  Flex,
  Loader,
  Modal,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "mantine-react-table";
import { IconSearch, IconTrash } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useDebouncedValue } from "@mantine/hooks";

type UserRow = {
  id: string;
  email: string;
  name: string;
  designation: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: Date;
  company: { id: string; legalName: string; gtid: string; status: string } | null;
};

const PAGE_SIZE = 15;

export default function AdminUsersPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const previousParamsRef = useRef<string>("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trpc.admin.listUsers.query({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setUsers(
        res.users.map((u) => ({
          ...u,
          createdAt: u.createdAt instanceof Date ? u.createdAt : new Date(u.createdAt as unknown as string),
        }))
      );
      setTotalRows(res.meta.total);
    } catch (err) {
      handleTrpcError(err);
      setUsers([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    const paramsString = JSON.stringify({
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search: debouncedSearch,
    });
    if (paramsString === previousParamsRef.current) {
      return;
    }
    previousParamsRef.current = paramsString;
    void fetchUsers();
  }, [fetchUsers, pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  }, [debouncedSearch]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await trpc.user.delete.mutate({ userId: deleteTarget.id });
      notifications.show({
        title: "User removed",
        message: `${deleteTarget.email} and related data were deleted.`,
        color: "green",
      });
      setDeleteTarget(null);
      await fetchUsers();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<UserRow>[]>(
    () => [
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        id: "company",
        header: "Company",
        Cell: ({ row }) => {
          const c = row.original.company;
          if (!c) return <Text c="dimmed" size="sm">—</Text>;
          return (
            <Text size="sm">
              {c.legalName}
              <Text span c="dimmed" size="xs" display="block">
                {c.gtid} · {c.status}
              </Text>
            </Text>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Joined",
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const d = cell.getValue<Date>();
          return <Text size="sm">{d.toLocaleDateString()}</Text>;
        },
      },
      {
        id: "actions",
        header: "",
        size: 60,
        Cell: ({ row }) => (
          <Tooltip label="Delete user and all related data">
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => setDeleteTarget(row.original)}
              aria-label="Delete user"
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ],
    []
  );

  return (
    <Flex direction="column" p="lg" gap="md" mih="100%">
      <div>
        <Title order={2}>Users</Title>
        <Text c="dimmed" size="sm" mt={4}>
          Client accounts. Deleting a user removes their company, collaborations, ledger entries, uploaded letters, and
          other linked records.
        </Text>
      </div>

      <TextInput
        placeholder="Search by email or name"
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        maw={360}
      />

      <MantineReactTable
        columns={columns}
        data={users}
        state={{
          isLoading: loading,
          pagination,
        }}
        onPaginationChange={setPagination}
        manualPagination
        rowCount={totalRows}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        mantineTableContainerProps={{ style: { minHeight: 400 } }}
      />

      {loading && users.length === 0 && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      <Modal
        opened={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete user"
        centered
      >
        {deleteTarget && (
          <>
            <Text size="sm">
              Permanently delete <strong>{deleteTarget.email}</strong>? This removes their company profile, interests,
              collaborations, wallet ledger, uploaded files, and cleans
              shortlist references. This cannot be undone.
            </Text>
            <Flex justify="flex-end" gap="sm" mt="lg">
              <Button variant="default" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </Button>
              <Button color="red" onClick={() => void confirmDelete()} loading={deleting}>
                Delete user
              </Button>
            </Flex>
          </>
        )}
      </Modal>
    </Flex>
  );
}
